// TimeCardSuggestions — the "Suggested" section and "My week" strip at the top of the time card
// (docs/ACTIVITY_HUD_PLAN.md steps 2, 3 and 5).
//
// Suggestions come from the person's own active work in the app and, once connected, their Google
// Calendar and Slack (their own messages in project channels). One suggestion per project per day.
// Nothing is logged until they press Add: that creates a normal Pending Review entry, exactly like a
// typed one, then the time card reloads. Add/Dismiss wait 5 seconds with Undo. A handled suggestion
// never returns: the server remembers the time it covered and only offers time added since.
//
// Follows the day the time card is showing via the `timecard-day` event runTimeCard dispatches.
import { useEffect, useState, useCallback, useRef } from 'react'
import { getSuggestions, markSuggestion, getMyWeek, getGoogleCalendarConnectUrl, disconnectGoogleCalendar } from '../services/activity.js'
import { createTimeEntry, getActivities, patchEntry } from '../services/timecards.js'
import { getSlackConnectUrl } from '../services/slack.js'
import { alertDialog, confirmDialog } from './Dialog.jsx'
import SearchSelect from './SearchSelect.jsx'

const font = '"DM Sans", sans-serif'
const mono = '"DM Mono", monospace'
const S = {
  wrap: { margin: '0 0 14px', padding: '12px 14px', border: '0.5px solid var(--aj-border)', borderRadius: 10, background: 'var(--aj-bg-surface)', fontFamily: font, fontSize: 13, color: 'var(--aj-text)' },
  head: { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 },
  title: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  muted: { color: 'var(--aj-text-muted)', fontSize: 12 },
  row: { display: 'grid', gridTemplateColumns: '108px 80px minmax(0,1fr) auto', gap: 8, alignItems: 'start', padding: '8px 0', borderTop: '0.5px solid var(--aj-border)' },
  input: { width: '100%', boxSizing: 'border-box', padding: '4px 6px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-page)', color: 'var(--aj-text)', fontFamily: font, fontSize: 12 },
  btn: { padding: '4px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', fontFamily: font, fontSize: 12, cursor: 'pointer' },
  primary: { background: '#07272D', color: '#fff', border: '0.5px solid #07272D' },
  note: { color: '#b7791f', fontSize: 12, marginTop: 4 },
  step: { flex: 1, width: 18, padding: 0, lineHeight: 1, fontSize: 8, border: '0.5px solid var(--aj-border-mid)', borderRadius: 3, background: 'var(--aj-bg-surface)', color: 'var(--aj-text-mid)', cursor: 'pointer' },
  connBox: { margin: '0 0 8px', padding: '6px 10px', border: '0.5px solid var(--aj-border)', borderRadius: 6, background: 'var(--aj-bg-page)' },
  connRow: { display: 'flex', gap: 10, alignItems: 'baseline', padding: '3px 0', fontSize: 12 },
  link: { background: 'none', border: 'none', padding: 0, color: 'var(--aj-accent-blue, #1D9E75)', cursor: 'pointer', fontFamily: font, fontSize: 12 },
}

function todayStr() {
  const d = new Date()
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

function MyWeek() {
  const [w, setW] = useState(null)
  useEffect(() => { getMyWeek().then(setW).catch(() => setW(null)) }, [])
  if (!w || !w.rows.length) return null
  const max = Math.max(...w.rows.map(r => r.hours), 0.1)
  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: '0.5px solid var(--aj-border)' }}>
      <div style={S.head}>
        <span style={S.title}>My week in the app</span>
        <span style={{ ...S.muted, fontFamily: mono }}>{w.totalHours} h</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,220px) 1fr 44px', gap: '4px 10px', alignItems: 'center' }}>
        {w.rows.map(r => (
          <div key={r.projectId || 'firm'} style={{ display: 'contents' }}>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12 }}>{r.projectName}</span>
            <span style={{ height: 6, borderRadius: 3, background: 'var(--aj-bg-muted)', overflow: 'hidden' }}>
              <i style={{ display: 'block', height: '100%', width: Math.round(100 * r.hours / max) + '%', background: 'var(--aj-accent, #07272D)' }} />
            </span>
            <span style={{ fontFamily: mono, fontSize: 12, textAlign: 'right' }}>{r.hours}</span>
          </div>
        ))}
      </div>
      <div style={{ ...S.muted, marginTop: 6 }}>Only time spent working in the app shows here.</div>
    </div>
  )
}

// The day's suggestions at a glance (Matt, 2026-10-19): total hours first, then hours by project,
// largest first. Counts what is still waiting, so it shrinks as suggestions are added or dismissed.
function SuggestionSummary({ list }) {
  const by = new Map()
  for (const s of list) {
    const name = s.projectName || (s.kind === 'meeting' ? 'Meetings' : 'Firm work')
    by.set(name, (by.get(name) || 0) + (Number(s.hours) || 0))
  }
  const rows = [...by.entries()].sort((a, b) => b[1] - a[1])
  const total = rows.reduce((t, r) => t + r[1], 0)
  const h = n => (Math.round(n * 10) / 10).toFixed(1)
  return (
    <div style={{ margin: '2px 0 4px', padding: '8px 10px', borderRadius: 6, background: 'var(--aj-bg-page)', border: '0.5px solid var(--aj-border)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontFamily: mono, fontSize: 15, fontWeight: 600 }}>{h(total)} h</span>
        <span style={S.muted}>suggested in {list.length} {list.length === 1 ? 'entry' : 'entries'}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 44px', gap: '2px 10px', marginTop: 6, fontSize: 12 }}>
        {rows.map(([name, hrs]) => (
          <div key={name} style={{ display: 'contents' }}>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
            <span style={{ fontFamily: mono, textAlign: 'right' }}>{h(hrs)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

const UNDO_MS = 5000

// One suggestion. Add and Dismiss wait UNDO_MS before anything is saved, with an Undo button, so a
// slip of the mouse costs nothing. Closing the time card during the wait saves it right away.
function SuggestionRow({ s, day, phases, codes, onDone }) {
  const [hours, setHours] = useState(String(s.hours))
  const [phaseId, setPhaseId] = useState(s.phaseId || '')
  const [memo, setMemo] = useState(s.memo || '')
  const [pending, setPending] = useState(null)      // null | 'add' | 'dismiss'
  const [left, setLeft] = useState(0)
  const timer = useRef(null), tick = useRef(null)
  // Up/down by 0.1 h, never below 0.1, always shown to one decimal.
  function nudge(dir) {
    setHours(h => { const n = Math.round(((Number(h) || 0) + dir * 0.1) * 10) / 10; return String(Math.max(0.1, n).toFixed(1)) })
  }
  const latest = useRef({})
  const phase = phases.find(p => p.id === phaseId)
  // The activity code follows the phase: overhead phases take OP- codes, project phases AS- codes.
  const autoCode = phase && phase.overhead
    ? (/^OP-/.test(s.activityCode) ? s.activityCode : (s.kind === 'meeting' ? 'OP- Communication' : 'OP- Administration'))
    : (/^AS-/.test(s.activityCode) ? s.activityCode : (s.kind === 'meeting' ? 'AS- Communication' : 'AS- Project Support'))
  // The person can change the code; a pick that no longer fits the phase falls back to the default.
  const [picked, setPicked] = useState(null)
  const fits = c => (phase && phase.overhead ? /^OP-/.test(c) : /^AS-/.test(c))
  const code = picked && fits(picked) ? picked : autoCode
  const codeOptions = (codes || []).filter(c => (phase ? fits(c) : /^(AS-|OP-)/.test(c)))
  latest.current = { hours, phaseId, memo, code, pending }
  // Merging only happens when nothing differs from the earlier entry (Matt, 2026-10-15).
  function mergeOk(v) {
    const m = s.mergeInto
    return !!m && v.phaseId === m.phaseId && v.code === m.activityCode && String(v.memo || '').trim() === String(m.memo || '').trim()
  }
  const willMerge = mergeOk({ phaseId, code, memo })
  // Project time carries no pre-filled note (Matt, 2026-10-19): the person writes it, client-facing.
  const needsNote = !!(phase ? !phase.overhead : s.projectId)

  async function commit(action) {
    clearTimeout(timer.current); clearInterval(tick.current); timer.current = null
    const v = latest.current
    if (action === 'add' && mergeOk(v)) {
      // Extra time on a project already added today: top up that draft entry instead of a second one.
      const m = s.mergeInto
      const total = Math.round((m.hours + Number(v.hours)) * 100) / 100
      try {
        const res = await patchEntry(m.id, { 'Actual Hours': total })
        if (res && res.error) throw new Error(res.error)
      } catch (e) {
        setPending(null)
        await alertDialog({ title: 'Could not add it', message: e.message || 'Something went wrong.' })
        return
      }
      try { await markSuggestion(day, s.key, 'accepted', m.id, s.covered) } catch { /* the entry is updated */ }
      window.dispatchEvent(new CustomEvent('timecard-refresh'))
      onDone(s.key)
      return
    }
    if (action === 'add') {
      const r = await createTimeEntry({
        date: day, hours: Number(v.hours), phaseId: v.phaseId, activityCode: v.code, memo: v.memo,
        reviewNotes: 'Added from a suggestion (' + (s.kind === 'meeting' ? 'calendar' : (s.sources || ['app activity']).join(', ')) + ', ' + s.when + ').',
        source: s.kind === 'meeting' ? 'calendar-suggestion' : 'activity-suggestion',
      })
      if (!r || r.error) {
        setPending(null)
        await alertDialog({ title: 'Could not add it', message: (r && r.error) || 'Something went wrong.' })
        return
      }
      try { await markSuggestion(day, s.key, 'accepted', r.id, s.covered) } catch { /* the entry exists */ }
      window.dispatchEvent(new CustomEvent('timecard-refresh'))
    } else {
      try { await markSuggestion(day, s.key, 'dismissed', null, s.covered) } catch { /* hide locally anyway */ }
    }
    onDone(s.key)
  }
  // Closing the time card mid-countdown: save now rather than lose the click.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (timer.current && latest.current.pending) commit(latest.current.pending) }, [])

  async function start(action) {
    if (action === 'add') {
      if (!(Number(hours) > 0)) { await alertDialog({ title: 'Hours needed', message: 'Enter the hours for this entry.' }); return }
      if (!phaseId) { await alertDialog({ title: 'Pick a phase', message: 'Choose which phase this time goes to.' }); return }
      if (needsNote && !memo.trim()) { await alertDialog({ title: 'Add a note', message: 'Say what you did, in words the client would see value in. This note goes on their invoice.' }); return }
    }
    setPending(action); setLeft(Math.round(UNDO_MS / 1000))
    tick.current = setInterval(() => setLeft(n => Math.max(0, n - 1)), 1000)
    timer.current = setTimeout(() => commit(action), UNDO_MS)
  }
  function undo() {
    clearTimeout(timer.current); clearInterval(tick.current); timer.current = null
    setPending(null)
  }

  const source = s.kind === 'meeting' ? 'Calendar' : (s.sources && s.sources.length ? s.sources.join(' · ') : 'Cortex')
  return (
    <div style={S.row}>
      <div style={{ opacity: pending ? 0.5 : 1 }}>
        <div style={{ fontFamily: mono, fontSize: 12 }}>{s.when}</div>
        <div style={S.muted}>{source}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
        <input style={{ ...S.input, fontFamily: mono }} value={hours} onChange={e => setHours(e.target.value)} inputMode="decimal" aria-label="Hours" disabled={!!pending}
          onKeyDown={e => { if (e.key === 'ArrowUp') { e.preventDefault(); nudge(1) } else if (e.key === 'ArrowDown') { e.preventDefault(); nudge(-1) } }} />
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <button type="button" style={S.step} onClick={() => nudge(1)} disabled={!!pending} aria-label="Add 0.1 hours" title="Add 0.1 h">&#9650;</button>
          <button type="button" style={S.step} onClick={() => nudge(-1)} disabled={!!pending} aria-label="Take off 0.1 hours" title="Take off 0.1 h">&#9660;</button>
        </span>
      </div>
      <div style={{ minWidth: 0, opacity: pending ? 0.5 : 1 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{s.kind === 'meeting' ? s.title : (s.projectName || 'Firm work')}</div>
        {/* Searchable (Matt, 2026-10-16): the phase list runs to every open phase in the firm. */}
        <SearchSelect style={S.input} value={phaseId} onChange={setPhaseId} disabled={!!pending} title="Phase" placeholder="Pick a phase…"
          options={[{ value: '', label: 'Pick a phase…' }, ...phases.map(p => ({ value: p.id, label: p.name }))]} />
        <SearchSelect style={{ ...S.input, marginTop: 4 }} value={code} onChange={setPicked} disabled={!!pending} title="Activity code"
          options={[...(!codeOptions.includes(code) ? [{ value: code, label: code }] : []), ...codeOptions.map(c => ({ value: c, label: c }))]} />
        <input style={{ ...S.input, marginTop: 4 }} value={memo} onChange={e => setMemo(e.target.value)} aria-label="Note" disabled={!!pending}
          placeholder={needsNote ? 'What you did, in words the client would value' : 'Note'} />
        {s.seen && <div style={{ ...S.muted, marginTop: 4 }}>In Cortex: {s.seen}</div>}
        {s.meetingCut && <div style={{ ...S.muted, marginTop: 4 }}>{s.meetingCut}, so it isn't counted twice.</div>}
        {s.slack && s.slack.length > 0 && <div style={{ ...S.muted, marginTop: 4 }}>In Slack: {s.slack.map(t => '“' + t + '”').join(' · ')}</div>}
        {s.did && s.did.length > 0 && <div style={{ ...S.muted, marginTop: 4 }}>Where these hours came from: {s.did.join('; ')}</div>}
        {s.emails && s.emails.length > 0 && <div style={{ ...S.muted, marginTop: 4 }}>Emails: {s.emails.join('; ')}</div>}
        {s.note && <div style={S.note}>{s.note}</div>}
        {s.mergeInto && (willMerge
          ? <div style={{ ...S.muted, marginTop: 4 }}>Adds to your earlier entry: {s.mergeInto.hours} h &rarr; {Math.round((s.mergeInto.hours + (Number(hours) || 0)) * 10) / 10} h</div>
          : <div style={{ ...S.muted, marginTop: 4 }}>Different from your earlier entry, so this will be a separate entry.</div>)}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {pending ? (
          <>
            <span style={{ ...S.muted, whiteSpace: 'nowrap' }}>{pending === 'add' ? 'Adding' : 'Dismissing'} in {left}s</span>
            <button style={{ ...S.btn, ...S.primary, opacity: 1 }} onClick={undo}>Undo</button>
          </>
        ) : (
          <>
            <button style={{ ...S.btn, ...S.primary }} onClick={() => start('add')}>Add</button>
            <button style={S.btn} onClick={() => start('dismiss')}>Dismiss</button>
          </>
        )}
      </div>
    </div>
  )
}

export default function TimeCardSuggestions({ initialDate }) {
  const [day, setDay] = useState(initialDate || todayStr())
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [gone, setGone] = useState(() => new Set())
  const [codes, setCodes] = useState([])
  // Which sources feed the suggestions — remembered per browser (a convenience, not a record).
  const [sources, setSources] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem('aj-suggestion-sources') || 'null')
      if (Array.isArray(v) && v.length) return v.includes('email') || localStorage.getItem('aj-suggestion-sources-email') ? v : [...v, 'email']
    } catch { /* storage blocked */ }
    return ['calendar', 'slack', 'cortex', 'email']
  })
  function toggleSource(k) {
    setSources(prev => {
      const next = prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]
      if (!next.length) return prev                  // keep at least one on
      try { localStorage.setItem('aj-suggestion-sources', JSON.stringify(next)); localStorage.setItem('aj-suggestion-sources-email', '1') } catch { /* storage blocked */ }
      return next
    })
  }
  useEffect(() => { getActivities().then(list => setCodes((list || []).map(a => a.label))).catch(() => {}) }, [])

  useEffect(() => {
    function onDay(e) { if (e && e.detail && e.detail.date) setDay(e.detail.date) }
    window.addEventListener('timecard-day', onDay)
    return () => window.removeEventListener('timecard-day', onDay)
  }, [])

  // Only the newest request may fill the panel: toggling chips quickly (or the day being set twice on
  // open) fires several, and an older answer arriving last used to replace the newer one.
  const reqSeq = useRef(0)
  const load = useCallback(() => {
    const n = ++reqSeq.current
    setData(null); setError('')
    getSuggestions(day, sources)
      .then(d => { if (n === reqSeq.current) setData(d) })
      .catch(e => { if (n === reqSeq.current) setError(e.message) })
  }, [day, sources])
  useEffect(() => { load() }, [load])
  // A phase was opened or closed for time entry: refresh the phase choices in place, keeping what is
  // already on screen (services/phases.js fires the event).
  useEffect(() => {
    function onPhases() {
      const n = reqSeq.current
      getSuggestions(day, sources).then(d => { if (n === reqSeq.current) setData(cur => (cur && d ? { ...cur, phases: d.phases } : d || cur)) }).catch(() => {})
    }
    window.addEventListener('phase-time-entry-changed', onPhases)
    return () => window.removeEventListener('phase-time-entry-changed', onPhases)
  }, [day, sources])

  async function connect() {
    try {
      const r = await getGoogleCalendarConnectUrl()
      if (r.notConfigured) { await alertDialog({ title: 'Not set up yet', message: 'Google Calendar connection has not been set up for the firm yet. Ask Matt.' }); return }
      window.location.href = r.url
    } catch (e) { await alertDialog({ title: 'Could not connect', message: e.message }) }
  }
  async function connectMail() {
    try {
      const r = await getGoogleCalendarConnectUrl('mail')
      if (r.notConfigured) { await alertDialog({ title: 'Not set up yet', message: 'Email connection has not been set up for the firm yet. Ask Matt.' }); return }
      const ok = await confirmDialog({
        title: 'Connect your sent email?',
        message: 'Your time card will suggest time from email you SEND to project clients, consultants and contractors, or with a project name in the subject. Only who it went to, the subject and the time are read. Never the message itself. Only you see the subjects. You can disconnect any time.',
        confirmLabel: 'Continue to Google',
      })
      if (ok) window.location.href = r.url
    } catch (e) { await alertDialog({ title: 'Could not connect', message: e.message }) }
  }
  async function disconnectMail() {
    if (!(await confirmDialog({ title: 'Disconnect email?', message: 'Sent email will stop feeding your suggestions. You can reconnect any time.', confirmLabel: 'Disconnect' }))) return
    await disconnectGoogleCalendar('mail').catch(() => {})
    load()
  }
  async function disconnect() {
    if (!(await confirmDialog({ title: 'Disconnect Google Calendar?', message: 'Meetings will stop appearing as suggestions. You can reconnect any time.', confirmLabel: 'Disconnect' }))) return
    await disconnectGoogleCalendar().catch(() => {})
    load()
  }

  async function connectSlack() {
    try {
      const token = window.__clerkGetToken ? await window.__clerkGetToken() : null
      const r = await getSlackConnectUrl(token)
      if (r && r.url) window.location.href = r.url
      else await alertDialog({ title: 'Could not connect Slack', message: (r && r.error) || 'No link came back.' })
    } catch (e) { await alertDialog({ title: 'Could not connect Slack', message: e.message }) }
  }

  const list = data ? data.suggestions.filter(s => !gone.has(s.key)) : []
  const cal = data && data.calendar
  const slack = data && data.slack
  const [showConn, setShowConn] = useState(false)
  // A read failure in words a person can act on. "API not enabled" is a firm Google setting, not
  // something reconnecting fixes, so it says so.
  const failText = (err) => /has not been used in project|is disabled/i.test(String(err || ''))
    ? "Can't read yet: the firm's Google settings need this switched on (an admin fix)"
    : "Couldn't read it last time"
  const mail = cal && cal.mail
  const adminFix = (err) => /has not been used in project|is disabled/i.test(String(err || ''))
  const connRows = []
  if (cal && cal.configured) connRows.push(cal.connected
    ? { key: 'cal', label: 'Calendar', status: cal.lastError ? failText(cal.lastError) : (cal.email || 'Connected'), problem: !!cal.lastError, action: cal.lastError ? (adminFix(cal.lastError) ? null : connect) : disconnect, actionLabel: cal.lastError ? 'Reconnect' : 'Disconnect' }
    : { key: 'cal', label: 'Calendar', status: 'Not connected', action: connect, actionLabel: 'Connect' })
  if (mail && mail.configured) connRows.push(mail.connected
    ? { key: 'mail', label: 'Email', status: mail.lastError ? failText(mail.lastError) : (mail.email || 'Connected') + ' · subject lines only', problem: !!mail.lastError, action: mail.lastError ? (adminFix(mail.lastError) ? null : connectMail) : disconnectMail, actionLabel: mail.lastError ? 'Reconnect' : 'Disconnect' }
    : { key: 'mail', label: 'Email', status: 'Not connected', action: connectMail, actionLabel: 'Connect' })
  if (slack) connRows.push(slack.connected && !slack.needsReconnect
    ? { key: 'slack', label: 'Slack', status: 'Connected' }
    : { key: 'slack', label: 'Slack', status: slack.connected ? 'Needs reconnecting to include messages' : 'Not connected', problem: !!slack.connected, action: connectSlack, actionLabel: slack.connected ? 'Reconnect' : 'Connect' })
  // Shown without opening Connections: only real problems with something you already connected.
  const needs = []
  if (cal && cal.connected && cal.lastError) needs.push({ key: 'cal', text: 'Calendar: ' + failText(cal.lastError) + '.', fix: () => setShowConn(true), fixLabel: 'Details' })
  if (mail && mail.connected && mail.lastError) needs.push({ key: 'mail', text: 'Email: ' + failText(mail.lastError) + '.', fix: () => setShowConn(true), fixLabel: 'Details' })
  if (slack && slack.connected && slack.needsReconnect) needs.push({ key: 'slack', text: 'Slack needs reconnecting.', fix: connectSlack, fixLabel: 'Reconnect' })
  const dayLabel = new Date(day + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })

  return (
    <div style={S.wrap}>
      {/* TIDY HEADER (2026-10-16, Matt: "this doesn't look tidy"): the title alone; the source chips;
          the connection details behind one "Connections" link; one plain line only when a source
          needs something from you. */}
      <div style={S.head}>
        <span style={S.title}>Suggested for {dayLabel}</span>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '0 0 6px', flexWrap: 'wrap' }}>
        <span style={S.muted}>Show from:</span>
        {[['calendar', 'Calendar'], ['slack', 'Slack'], ['email', 'Email'], ['cortex', 'Cortex']].map(([k, label]) => {
          const on = sources.includes(k)
          return (
            <button key={k} onClick={() => toggleSource(k)} aria-pressed={on} style={{
              padding: '2px 10px', borderRadius: 20, fontFamily: font, fontSize: 12, cursor: 'pointer',
              border: '0.5px solid ' + (on ? '#07272D' : 'var(--aj-border-mid)'),
              background: on ? '#07272D' : 'transparent', color: on ? '#fff' : 'var(--aj-text-muted)',
            }}>{label}</button>
          )
        })}
        <button style={{ ...S.link, marginLeft: 'auto' }} onClick={() => setShowConn(v => !v)} aria-expanded={showConn}>{showConn ? 'Hide connections' : 'Connections'}</button>
      </div>
      {needs.length > 0 && !showConn && (
        <div style={{ ...S.note, margin: '0 0 6px' }}>
          {needs.map((n, i) => <span key={n.key}>{i ? ' · ' : ''}{n.text} <button style={S.link} onClick={n.fix}>{n.fixLabel}</button></span>)}
        </div>
      )}
      {showConn && (
        <div style={S.connBox}>
          {connRows.map(r => (
            <div key={r.key} style={S.connRow}>
              <span style={{ width: 70, color: 'var(--aj-text)' }}>{r.label}</span>
              <span style={{ flex: 1, minWidth: 0, color: r.problem ? '#b7791f' : 'var(--aj-text-muted)' }}>{r.status}</span>
              {r.action && <button style={S.link} onClick={r.action}>{r.actionLabel}</button>}
            </div>
          ))}
        </div>
      )}
      {error && <div style={{ color: '#c0392b', fontSize: 12 }}>{error}</div>}
      {!data && !error && <div style={S.muted}>Looking at your day…</div>}
      {data && !list.length && (
        <div style={S.muted}>
          Nothing to suggest for this day. Suggestions come from the time you spend working in the app{cal && cal.connected ? ', your calendar' : ''}{slack && slack.connected && !slack.needsReconnect ? ' and your Slack messages in project channels' : ''}.
        </div>
      )}
      {data && list.length > 0 && <SuggestionSummary list={list} />}
      {data && list.map(s => (
        // The hours are part of the key: a card keeps its typed hours in its own state, so new hours
        // for the same span must start a fresh card or it would disagree with the summary above.
        <SuggestionRow key={s.key + '|' + s.hours} s={s} day={day} phases={data.phases} codes={codes}
          onDone={k => setGone(prev => new Set([...prev, k]))} />
      ))}
      {data && list.length > 0 && (
        <div style={{ ...S.muted, marginTop: 6 }}>One suggestion per project per day. Nothing is logged until you press Add, and Add or Dismiss can be undone for 5 seconds. Hours count the time you were working, with breaks over 10 minutes left out.</div>
      )}
      <MyWeek />
    </div>
  )
}
